import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Building2, Save, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { BACKEND_URL } from '../lib/api';
import ImageUploader from './ImageUploader';

/**
 * Inline organiser profile setup. Renders as a modal-like card the
 * dashboard can mount conditionally. POSTing fills the profile and
 * unblocks event creation.
 */
const OrganiserProfileForm = ({ existing, onSaved, onClose }) => {
  const [form, setForm] = useState({
    organisation_name: existing?.organisation_name || '',
    bio: existing?.bio || '',
    logo_url: existing?.logo_url || '',
    cover_image_url: existing?.cover_image_url || '',
    region: existing?.region || '',
    website: existing?.website || '',
    contact_email: existing?.contact_email || '',
    contact_phone: existing?.contact_phone || '',
    instagram: existing?.instagram || '',
    facebook: existing?.facebook || '',
    public_profile_enabled:
      existing?.public_profile_enabled === undefined ? true : !!existing.public_profile_enabled,
  });
  const [saving, setSaving] = useState(false);

  // Lock page scroll behind the modal so small viewports don't show the
  // dashboard scrolling sideways underneath. Cleans up on close.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevOverflowX = document.body.style.overflowX;
    document.body.style.overflow = 'hidden';
    document.body.style.overflowX = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.overflowX = prevOverflowX;
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if ((form.organisation_name || '').trim().length < 2) {
      toast.error('Please enter your organisation name.');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/organiser/profile`, form, { withCredentials: true });
      toast.success(existing ? 'Profile updated' : 'Profile created — you can now create events.');
      onSaved?.(res.data?.profile);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save your profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-start sm:items-center justify-center"
      style={{ padding: '16px', paddingTop: '24px', paddingBottom: '24px' }}
      data-testid="profile-modal"
      onClick={() => !saving && onClose?.()}
    >
      <div
        className="bg-white rounded-2xl w-full shadow-xl flex flex-col"
        style={{
          maxWidth: 'min(100%, 720px)',
          maxHeight: 'calc(100dvh - 32px)',
          overscrollBehavior: 'contain',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 sm:px-8 pt-6 sm:pt-8 pb-4 border-b border-stone-100 bg-white rounded-t-2xl">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-[#F4F6F3] flex items-center justify-center shrink-0">
              <Building2 className="w-5 h-5 text-[#1F3D2B]" />
            </div>
            <div className="min-w-0">
              <h3 className="font-serif text-2xl text-[#1F3D2B] truncate">{existing ? 'Edit Organiser Profile' : 'Create Your Organiser Profile'}</h3>
              <p className="text-sm text-stone-500">Required before you can list events.</p>
            </div>
          </div>
          {onClose && (
            <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-600 shrink-0 ml-2" data-testid="profile-modal-close">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <form onSubmit={submit} className="space-y-4 overflow-y-auto px-6 sm:px-8 py-6">
          <div>
            <Label htmlFor="org_name">Organisation Name *</Label>
            <Input
              id="org_name"
              data-testid="profile-organisation-name"
              value={form.organisation_name}
              onChange={(e) => setForm({ ...form, organisation_name: e.target.value })}
              placeholder="e.g. Stellenbosch Cellars"
              required
            />
          </div>
          <div>
            <Label htmlFor="bio">Short Bio</Label>
            <Textarea
              id="bio"
              data-testid="profile-bio"
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              placeholder="A few sentences about your venue or experiences. Shown on your event pages."
              rows={3}
            />
          </div>
          {/* Image uploads — outside the dense grid so they have room to breathe */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ImageUploader
              label="Logo / profile image"
              value={form.logo_url}
              onChange={(url) => setForm({ ...form, logo_url: url })}
              aspect="square"
              testId="profile-logo-uploader"
            />
            <ImageUploader
              label="Cover image / banner"
              value={form.cover_image_url}
              onChange={(url) => setForm({ ...form, cover_image_url: url })}
              aspect="wide"
              testId="profile-cover-uploader"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="region">Location / region</Label>
              <Input
                id="region"
                value={form.region}
                onChange={(e) => setForm({ ...form, region: e.target.value })}
                placeholder="Stellenbosch, Western Cape"
                data-testid="profile-region"
              />
            </div>
            <div>
              <Label htmlFor="website">Website</Label>
              <Input id="website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="vineyard.co.za" />
            </div>
            <div>
              <Label htmlFor="public_email">Public enquiry email</Label>
              <Input
                id="public_email"
                type="email"
                value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                placeholder="hello@vineyard.co.za"
                data-testid="profile-contact-email"
              />
            </div>
            <div>
              <Label htmlFor="phone">Contact phone (optional, not shown publicly)</Label>
              <Input id="phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="+27 21 555 0000" />
            </div>
            <div>
              <Label htmlFor="ig">Instagram handle</Label>
              <Input id="ig" value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} placeholder="@vineyard" />
            </div>
            <div>
              <Label htmlFor="fb">Facebook page</Label>
              <Input id="fb" value={form.facebook} onChange={(e) => setForm({ ...form, facebook: e.target.value })} placeholder="facebook.com/vineyard" />
            </div>
          </div>

          {/* Public profile toggle */}
          <label
            htmlFor="public_toggle"
            className="flex items-start gap-3 p-4 bg-[#F4F6F3] border border-[#E5E9E6] rounded-xl cursor-pointer"
            data-testid="profile-public-toggle-row"
          >
            <input
              id="public_toggle"
              type="checkbox"
              checked={form.public_profile_enabled}
              onChange={(e) => setForm({ ...form, public_profile_enabled: e.target.checked })}
              className="mt-1 h-4 w-4 accent-[#1F3D2B]"
              data-testid="profile-public-toggle"
            />
            <span className="text-sm text-[#1F3D2B]">
              <span className="font-medium block">Show my organiser profile publicly</span>
              <span className="text-xs text-[#4A5D50]">
                When enabled, guests on your event pages can click your name to see your public profile and other upcoming events.
                {existing?.slug && (
                  <> Your public URL: <span className="font-mono text-[#1F3D2B]">/organiser/{existing.slug}</span></>
                )}
              </span>
            </span>
          </label>

          <div className="flex justify-end gap-2 pt-4 border-t border-stone-100">
            {onClose && (
              <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
            )}
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full px-6"
              data-testid="profile-save"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving…' : existing ? 'Save changes' : 'Create profile'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrganiserProfileForm;
